import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Hero from '@/components/Hero';
import SEO from '@/components/SEO';
import { AllPartnersTrustBar } from '@/components/TrustBar';
import { fadeInUp, fadeIn, staggerContainer, slideInRight } from '@/motion/variants';
import { TrendingUp, Users, Award, Sparkles, Target, FileText, Clock, Cloud, BarChart3, Shield, CreditCard, Ship } from 'lucide-react';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { TradingTrustStrip } from '@/components/TradingTrustStrip';
import { FeatureBadges } from '@/components/MicroTrustBadges';

/** Homepage: SEO uses default brand image. For best link previews, add public/og-home.jpg (1200×630) and set image="/og-home.jpg". */
const Index = () => {
  return (
    <div className="min-h-screen bg-background pt-6">
      <SEO
        title="Padmora — AI-powered commodity trading & trade finance | USA"
        description="Boutique trading firm: LC-backed deals, FOB→CFR, bank-grade compliance. $1M+ closed, 35% labor cost reduction. Request a quote."
        image="/android-chrome-512x512.png"
        canonical="https://padmora.com/"
        keywords={[
          "commodity trading",
          "letter of credit",
          "FOB to CFR",
          "ammonium sulphate",
          "AI trading platform",
          "US trading firm",
          "UCP600 compliant",
          "commodity finance",
          "trade finance",
          "supply chain automation",
          "LC-backed deals",
          "documentary credit"
        ]}
        aiOptimization={{
          aiKeywords: [
            "artificial intelligence commodity trading",
            "machine learning trade execution",
            "automated compliance screening",
            "predictive analytics commodities",
            "AI-powered supply chain"
          ],
          entities: [
            "United States",
            "United States",
            "Ammonium Sulphate",
            "Letter of Credit",
            "UCP600",
            "Trade Finance",
            "Commodity Trading"
          ],
          topics: [
            "Commodity Trading",
            "Trade Finance",
            "AI Automation",
            "Supply Chain Management",
            "Compliance Management"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Padmora",
          "description": "US boutique trading firm specializing in AI-powered commodity deals with LC-backed financing and full compliance automation.",
          "url": "https://padmora.com",
          "logo": "https://padmora.com/android-chrome-512x512.png",
          "foundingDate": "2025",
          "founders": [
            {
              "@type": "Person",
              "name": "Michel Padron Morales"
            }
          ],
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "US",
            "addressRegion": "Wyoming"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "availableLanguage": ["English"],
            "email": "hello@padmora.com"
          },
          "sameAs": [
            "https://www.linkedin.com/company/padmora"
          ],
          "knowsAbout": [
            "Commodity Trading",
            "Trade Finance",
            "AI Automation",
            "Supply Chain Management",
            "Letter of Credit",
            "UCP600 Compliance"
          ]
        }}
        performance={{
          preload: [
            "/android-chrome-512x512.png",
            "/hero-bg.jpg"
          ],
          dnsPrefetch: [
            "fonts.googleapis.com",
            "fonts.gstatic.com"
          ],
          preconnect: [
            "fonts.googleapis.com",
            "fonts.gstatic.com"
          ]
        }}
      />
      <Navigation />

      {/* New Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-6 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="text-center max-w-6xl mx-auto"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-foreground mb-8 leading-tight"
            >
              Specialised Trading Firm
            </motion.h1>

            <motion.p
              variants={fadeIn}
              className="text-xl text-secondary mb-6 max-w-4xl mx-auto"
            >
              We structure LC-backed commodity deals (FOB→CFR) with bank-grade compliance and full document control.
            </motion.p>

            <motion.div
              variants={slideInRight}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6"
            >
              <Button asChild variant="outline" size="lg" className="min-w-[200px]">
                <Link to="/trading">Explore Trading</Link>
              </Button>
              <Button asChild size="lg" className="min-w-[200px]">
                <Link to="/consumer-pack">Consumer Pack → Caribbean</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="min-w-[200px]">
                <Link to="/compliance">Compliance & LC Policy</Link>
              </Button>
            </motion.div>
          </motion.div>

          <TradingTrustStrip />

          {/* Partners at bottom of hero */}
          <div className="mt-16">
            <AllPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Three Key Cards */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="text-center bg-card/50 border border-border/50 p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <CreditCard className="h-12 w-12 text-blue-600 mb-4 mx-auto" />
              <h3 className="text-xl font-semibold mb-4">Letters of Credit</h3>
              <p className="text-slate-600">Irrevocable, transferable, confirmed LCs under UCP600</p>
            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="text-center bg-card/50 border border-border/50 p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <Shield className="h-12 w-12 text-blue-600 mb-4 mx-auto" />
              <h3 className="text-xl font-semibold mb-4">Standard Inspections & Insurance</h3>
              <p className="text-slate-600">SGS quality & quantity inspections with ICC(A) marine insurance</p>
            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="text-center bg-card/50 border border-border/50 p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <Ship className="h-12 w-12 text-blue-600 mb-4 mx-auto" />
              <h3 className="text-xl font-semibold mb-4">Forward CFR Deals & Market Timing</h3>
              <p className="text-slate-600">Strategic commodity trading with precise timing for optimal market conditions</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trading and Platform Summary */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-lg transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900">AI Platform</h3>
              <p className="text-slate-600 mb-6">
                Our internal AI platform that makes trading faster and safer with predictive signals and compliance automation
              </p>
              <FeatureBadges
                features={[
                  { icon: <Sparkles className="h-4 w-4" />, label: "Spread Radar: live CFR vs FOB" },
                  { icon: <FileText className="h-4 w-4" />, label: "LC Checker: risk flag clauses" },
                  { icon: <Ship className="h-4 w-4" />, label: "Fixture Planner: laycan optimization" },
                  { icon: <Shield className="h-4 w-4" />, label: "KYC Engine: sanctions screening" }
                ]}
                align="left"
                className="mt-4"
              />
              <Link
                to="/platform"
                className="inline-block mt-6 text-blue-600 hover:text-blue-700 font-semibold"
              >
                Learn more →
              </Link>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-lg transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900">LC-Backed Trading</h3>
              <p className="text-slate-600 mb-6">
                Convert FOB supply into CFR delivery using transferable/confirmed LCs with standard inspections
              </p>
              <FeatureBadges
                features={[
                  { icon: <CreditCard className="h-4 w-4" />, label: "Transferable LC (UCP600)" },
                  { icon: <Shield className="h-4 w-4" />, label: "SGS & ICC(A) standards" },
                  { icon: <FileText className="h-4 w-4" />, label: "Document control" },
                  { icon: <Ship className="h-4 w-4" />, label: "Shipbroker execution" }
                ]}
                align="left"
                className="mt-4"
              />
              <Link
                to="/trading"
                className="inline-block mt-6 text-blue-600 hover:text-blue-700 font-semibold"
              >
                Learn more →
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold mb-6 text-slate-900"
            >
              Ready to Transform Your Business?
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              Join leading companies leveraging AI-powered trading solutions. Get started with our managed services today.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Button
                asChild
                size="lg"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
              >
                <Link to="/trading">Check Trading</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="px-8 py-4 text-lg border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                <Link to="/platform">Explore our platform</Link>
              </Button>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Index;
